// Google Drive for backups (Q7): "Save to Google Drive" puts a copy in the "Portfolio Ledger exports"
// folder, and "Restore from backup" can choose one of those copies. Access is per file (the drive.file
// permission): the app sees only the files it saved, never the rest of your Drive. The
// Google permission prompt appears the first time you use either, and lasts about an hour each time.
/* eslint-disable @typescript-eslint/no-explicit-any */

const CLIENT_ID = '399501604642-ol9vrjoqubmgeqdmk51msd0ubuqqnkhl.apps.googleusercontent.com';   // public, not a secret
const SCOPE = 'https://www.googleapis.com/auth/drive.file';
const FOLDER = 'Portfolio Ledger exports';

declare const google: any;

const scripts: Record<string, Promise<void>> = {};
function loadScript(src: string): Promise<void> {
  return scripts[src] || (scripts[src] = new Promise((res, rej) => {
    const s = document.createElement('script'); s.src = src; s.async = true;
    s.onload = () => res(); s.onerror = () => { delete scripts[src]; rej(new Error('Couldn’t reach Google. Check your connection.')); };
    document.head.appendChild(s);
  }));
}

let token: { value: string; until: number } | null = null;
let hint = '';
/** Remembers who's signed in (so Google suggests the same account) and loads Google's sign-in script
 *  ahead of time: the permission popup must open straight from your tap, or browsers block it. */
export function setDriveUser(email: string) {
  hint = email;
  const warm = () => { loadScript('https://accounts.google.com/gsi/client').catch(() => {}); };
  if ('requestIdleCallback' in window) (window as any).requestIdleCallback(warm, { timeout: 5000 }); else setTimeout(warm, 2000);
}

/** A Drive access token for this person, asking Google (a popup) when there isn't a current one. */
async function getToken(): Promise<string> {
  if (token && Date.now() < token.until) return token.value;
  await loadScript('https://accounts.google.com/gsi/client');
  return new Promise((res, rej) => {
    const client = google.accounts.oauth2.initTokenClient({
      client_id: CLIENT_ID, scope: SCOPE, hint, prompt: '',
      callback: (r: any) => {
        if (r.error || !r.access_token) { rej(new Error(r.error === 'access_denied' ? 'Google Drive access wasn’t allowed' : r.error_description || r.error || 'Google sign-in failed')); return; }
        token = { value: r.access_token, until: Date.now() + (Number(r.expires_in) || 3600) * 1000 - 60e3 };
        res(token.value);
      },
      error_callback: (e: any) => rej(new Error(e && e.type === 'popup_closed' ? 'The Google window was closed' : e && e.type === 'popup_failed_to_open' ? 'Allow pop-ups for this site, then try again' : 'Google sign-in failed')),
    });
    client.requestAccessToken();
  });
}

async function api(path: string, init: RequestInit = {}): Promise<any> {
  const t = await getToken();
  const r = await fetch('https://www.googleapis.com' + path, { ...init, headers: { Authorization: 'Bearer ' + t, ...(init.headers || {}) } });
  if (r.status === 401) token = null;
  if (!r.ok) { let m = 'error ' + r.status; try { const j = await r.json(); m = j.error?.message || m; } catch { /* keep */ } throw new Error(m); }
  return r;
}

async function folderId(): Promise<string> {
  const q = encodeURIComponent(`name = '${FOLDER}' and mimeType = 'application/vnd.google-apps.folder' and trashed = false`);
  const j = await (await api(`/drive/v3/files?q=${q}&fields=files(id)&spaces=drive`)).json();
  if (j.files && j.files[0]) return j.files[0].id;
  const c = await (await api('/drive/v3/files?fields=id', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ name: FOLDER, mimeType: 'application/vnd.google-apps.folder' }) })).json();
  return c.id;
}

/** Saves a file to the "Portfolio Ledger exports" folder; returns its "Open in Drive" link. */
export async function saveToDrive(name: string, data: string, mime: string): Promise<string> {
  const parent = await folderId(), b = 'pl' + Math.random().toString(36).slice(2);
  const body = `--${b}\r\nContent-Type: application/json; charset=UTF-8\r\n\r\n${JSON.stringify({ name, mimeType: mime, parents: [parent] })}\r\n` +
    `--${b}\r\nContent-Type: ${mime}; charset=UTF-8\r\n\r\n${data}\r\n--${b}--`;
  const j = await (await api('/upload/drive/v3/files?uploadType=multipart&fields=id,webViewLink', { method: 'POST', headers: { 'Content-Type': 'multipart/related; boundary=' + b }, body })).json();
  return j.webViewLink || 'https://drive.google.com/file/d/' + j.id + '/view';
}

/** The backups in the "Portfolio Ledger exports" folder that this app saved, newest first. (Google
 *  lets the app see only files it saved itself, never the rest of your Drive.) */
export async function listBackups(): Promise<{ id: string; name: string; modified: string }[]> {
  const q = encodeURIComponent(`name contains 'portfolio-backup' and mimeType = 'application/json' and trashed = false`);
  const j = await (await api(`/drive/v3/files?q=${q}&orderBy=modifiedTime desc&pageSize=20&fields=files(id,name,modifiedTime)&spaces=drive`)).json();
  return (j.files || []).map((f: any) => ({ id: f.id, name: f.name, modified: f.modifiedTime }));
}
export async function readBackup(id: string): Promise<string> {
  return (await api(`/drive/v3/files/${encodeURIComponent(id)}?alt=media`)).text();
}
